import { store } from './store.js';
// ========================================
// NARRATIVE AGENT - FASE 5
// Agente Narrativo (Storyteller)
// ========================================

/**
 * Agente Narrativo
 * Convierte estructuras matemáticas en micro-relatos educativos
 * usando Gemini API con directrices pedagógicas específicas.
 */

/**
 * Enriquece estructuras matemáticas con narrativas contextuales
 * @param {Array} mathData - Array de estructuras matemáticas del MathAgent
 * @param {string} contextId - ID del contexto seleccionado
 * @param {string} level - Nivel de dificultad
 * @param {string} language - Código de idioma (ej: 'es-ES')
 * @returns {Promise<Array>} Array de problemas enriquecidos con narrativa
 */
async function enrichWithNarrative(mathData, contextId, level, language = 'es-ES') {
    console.log(`📖 [NarrativeAgent] Enriqueciendo con contexto: ${contextId}...`);

    // Verificar que la API está configurada
    if (!isApiKeyConfigured || !isApiKeyConfigured()) {
        console.warn('⚠️ [NarrativeAgent] API no configurada, usando narrativas básicas');
        return applyBasicNarratives(mathData, contextId, language);
    }

    try {
        const systemInstruction = buildNarrativeSystemInstruction(contextId, level, language);
        const userPrompt = buildNarrativePrompt(mathData, contextId);

        let attempts = 0;
        let enrichedData = null;
        let lastRawData = null;
        const maxAttempts = 3;

        // Sistema de reintentos con validación semántica
        while (attempts < maxAttempts) {
            attempts++;
            console.log(`🔄 [NarrativeAgent] Intento ${attempts}/${maxAttempts}...`);

            // Llamar a Gemini API con JSON mode
            const rawData = await callGeminiWithJsonMode(userPrompt, systemInstruction);
            lastRawData = rawData;

            // Validar coherencia semántica de cada narrativa
            let allValid = true;
            const validationResults = [];

            if (typeof validateNarrativeCoherence !== 'undefined') {
                for (const narrative of rawData) {
                    const validation = validateNarrativeCoherence(narrative.story_text, contextId);
                    validationResults.push(validation);

                    if (!validation.isValid) {
                        console.warn(`⚠️ [NarrativeAgent] Incoherencia detectada (intento ${attempts}/${maxAttempts}):`, validation.issues);
                        console.warn(`   Texto: "${narrative.story_text.substring(0, 100)}..."`);
                        allValid = false;
                    }

                    // Registrar estadísticas
                    if (typeof validationStats !== 'undefined') {
                        validationStats.record(narrative.story_text, validation);
                    }
                }
            } else {
                // Si no hay validador, aceptar el primer resultado
                console.warn('⚠️ [NarrativeAgent] Validador semántico no disponible, aceptando resultado sin validación');
                allValid = true;
            }

            if (allValid) {
                enrichedData = rawData;
                console.log(`✅ [NarrativeAgent] Narrativas validadas exitosamente en intento ${attempts}`);
                break;
            }

            // Si no es el último intento, esperar un poco antes de reintentar
            if (attempts < maxAttempts) {
                console.log(`⏳ [NarrativeAgent] Esperando antes de reintentar...`);
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        // Si no se logró validación tras todos los intentos, usar última generación con advertencia
        if (!enrichedData) {
            console.warn(`⚠️ [NarrativeAgent] No se logró validación completa tras ${maxAttempts} intentos`);
            console.warn('   Usando última generación con posibles incoherencias');
            enrichedData = lastRawData;
        }

        // Validar y combinar con datos originales
        const finalData = mergeMathAndNarrative(mathData, enrichedData);

        console.log(`✅ [NarrativeAgent] Narrativas generadas para ${finalData.length} problemas (${attempts} intentos)`);
        return finalData;

    } catch (error) {
        console.error('❌ [NarrativeAgent] Error:', error);
        console.warn('⚠️ Fallback a narrativas básicas');
        return applyBasicNarratives(mathData, contextId, language);
    }
}

/**
 * Construye la instrucción del sistema para Gemini
 */
function buildNarrativeSystemInstruction(contextId, level, language) {
    const context = CONTEXTS.find(ctx => ctx.id === contextId);
    const contextName = context ? context?.title?.[language] : contextId;

    // Detectar nombre del idioma y configuración específica
    let languageName = 'Español';

    // Mapa de nombres de idioma para el prompt
    const langMap = {
        'es-ES': 'Español (España)', 'ca-ES': 'Catalán', 'eu-ES': 'Euskera', 'gl-ES': 'Gallego',
        'en-GB': 'Inglés (Británico)', 'fr-FR': 'Francés', 'de-DE': 'Alemán', 'it-IT': 'Italiano',
        'pt-PT': 'Portugués', 'zh-CN': 'Chino Mandarín', 'hi-IN': 'Hindi', 'ar-XA': 'Árabe'
    };

    if (typeof LANGUAGES !== 'undefined' && LANGUAGES[language]) {
        languageName = LANGUAGES[language].name;
    } else {
        languageName = langMap[language] || 'Español';
    }

    // Configuración específica por idioma
    let languageGuidance = 'Castellano neutro de España. Gramática perfecta.';

    if (language === 'es-ES') languageGuidance = 'Castellano neutro de España. Gramática perfecta, concordancia de género (cuántos/cuántas) y número estricta.';
    else if (language === 'en-GB') languageGuidance = 'Inglés Británico. Gramática perfecta, concordancia sujeto-verbo correcta.';
    else if (language === 'ca-ES') languageGuidance = 'Catalán. Gramática perfecta, pronombres febles correctos.';
    else if (language === 'gl-ES') languageGuidance = 'Gallego. Normativa oficial RAG.';
    else if (language === 'eu-ES') languageGuidance = 'Euskera (Euskara Batua). Gramática y declinaciones correctas.';
    else languageGuidance = `${languageName}. Gramática y ortografía perfectas en este idioma.`;

    const levelGuidance = {
        'beginner': `Nivel APRENDIZ (Iniciación):
- Lenguaje: MUY simple, cercano y directo para niños de 6-7 años
- LONGITUD OBJETIVO: 3-4 frases cortas y motivadoras (40-60 palabras total)
- Estructura: Situación emocionante + acción clara + pregunta
- Vocabulario: Palabras básicas y positivas
- Contexto: Ir de compras con familia, elegir, meter en cesta, llevar a casa
- Image_prompt: EXTREMADAMENTE explícito sobre cantidad y disposición`,

        'advanced': `Nivel AVANZADO (Desarrollo):
- Lenguaje: Claro, motivador y con vocabulario variado
- LONGITUD OBJETIVO: 4-5 frases con más detalles emocionales (60-80 palabras total)
- Estructura: Contexto situacional motivador + desarrollo con detalles + pregunta
- Vocabulario: Emociones, sensaciones
- Contexto: Experiencias de compra, ayudar, elegir, descubrir, encontrar
- Image_prompt: Detallado con elementos del contexto y emociones`,

        'expert': `Nivel EXPERTO (Ampliación):
- Lenguaje: Rico, descriptivo y emocionalmente conectado, para 7 años
- LONGITUD OBJETIVO: 5-6 frases narrativas inmersivas (80-100 palabras total)
- Estructura: Mini-historia con situación emocionante + desarrollo + sorpresa/giro + pregunta
- Vocabulario: Muy descriptivo infantil
- Contexto: Aventura de compras, compartir, ayudar, decisiones, regalos sorpresa
- Image_prompt: Escena ambiental rica e ilustrativa con detalles emocionales`
    };

    // Obtener verbos y elementos específicos del contexto
    // Nota: Usamos los de español como referencia semántica, pero instruimos al agente a TRADUCIR.
    const verbTable = typeof CONTEXT_VERB_TABLES !== 'undefined' ? CONTEXT_VERB_TABLES[contextId] : null;

    // Seleccionar verbos y elementos de muestra
    const exampleVerbs = verbTable ? verbTable.verbs.slice(0, 12).join(', ') : '';
    const exampleElements = verbTable ? verbTable.elements.slice(0, 15).join(', ') : '';

    // Crear sección de vocabulario
    const vocabularySection = verbTable ? `

📚 VOCABULARIO (Referencia en Español - **TRADUCIR Y ADAPTAR AL IDIOMA: ${languageName}**):

**VERBOS SUGERIDOS** (Usa su equivalente natural en ${languageName}):
${exampleVerbs}

**ELEMENTOS ESPECÍFICOS** (Usa su equivalente natural en ${languageName}):
${exampleElements}

🚫 **PROHIBIDO ABSOLUTAMENTE**:
- "tiene" y "le da" / "le dio" (o equivalentes simples en ${languageName})
- Usar siempre los mismos verbos.
- "Ana" y "Pedro" como nombres (Usa nombres variados y culturalmente adecuados al idioma ${languageName})

✅ **REGLAS GRAMATICALES OBLIGATORIAS**:
- Asegura la concordancia correcta de género y número en ${languageName}.
- Usa estructuras de frase naturales y nativas, NO traducciones literales.
` : '';

    // NUEVA SECCIÓN: Política de No Violencia
    const nonViolencePolicy = `

🛡️ **POLÍTICA DE NO VIOLENCIA OBLIGATORIA**:
Esta aplicación está dirigida a niños de 6-7 años y DEBE ser completamente segura y pacífica.
**PROHIBIDO**: Armas (incluso de juguete), violencia, peleas, conflictos bélicos.
**PERMITIDO**: Cooperación, amistad, naturaleza, disfraces pacíficos, deportes.
Si generas algo violento, DETENTE y REGENERA.
`;

    return `SYSTEM_INSTRUCTION: NARRATIVE_AGENT_V1

ROL: Escritor de literatura infantil y pedagogo experto en educación matemática para niños de 6-7 años.
IDIOMA DE SALIDA: **${languageName}** (${language})

CONTEXTO NARRATIVO: "${contextName}"
Todos los problemas deben ambientarse coherentemente en este contexto.

AUDIENCIA: Niños de 6-7 años.
- Lenguaje: **${languageGuidance}**
- Vocabulario: Apropiado para primer grado, palabras que usan los niños diariamente en ${languageName}.
- **Lenguaje infantil**: Usa palabras emocionales y descriptivas propias del idioma.
- Nombres: Diversos y apropiados para el idioma ${languageName}.

${levelGuidance[level] || levelGuidance['beginner']}

LENGUAJE MOTIVADOR PARA NIÑOS (Adapta a ${languageName}):
- **EVITA** lenguaje formal, pasivo o burocrático.
- **USA** lenguaje activo, directo y alegre.

${vocabularySection}
${nonViolencePolicy}

📖 ESTRUCTURA LINEAL-TEMPORAL (OBLIGATORIA):
Cada problema DEBE seguir esta estructura exacta en 4 pasos (en ${languageName}):

1. **ESCENARIO** (1 frase): Sitúa al niño en la situación
2. **CANTIDAD INICIAL** (1 frase): Presenta el dato inicial claro
3. **ACCIÓN/CAMBIO** (1-2 frases): Algo ocurre que cambia la cantidad (verbo de acción)
4. **PREGUNTA FINAL** (1 frase): Pregunta clara sobre la cantidad final

IMPORTANTE: 
- El texto debe ser fluido y natural en **${languageName}**.
- Revisa meticulosamente la ortografía y gramática en **${languageName}**.
- **CONCORDANCIA**: Verifica rigurosamente género, número y persona.

SALIDA: Array JSON con 5 objetos. Cada objeto DEBE tener:
{
  "id": número_del_problema,
  "story_text": "Enunciado narrativo completo en ${languageName} incluyendo la pregunta al final",
  "image_prompt": "Descripción detallada en inglés para generación de imagen educativa estilo cartoon",
  "question": "Pregunta específica extraída en ${languageName}"
}

IMPORTANTE: Responde SOLO con el JSON. El campo "story_text" debe estar en **${languageName}**.`;
}

/**
 * Construye el prompt del usuario con los datos matemáticos
 */
function buildNarrativePrompt(mathData, contextId) {
    const mathSummary = mathData.map(m => ({
        id: m.id,
        tipo: m.logic_type,
        operandos: m.operands,
        respuesta_correcta: m.correct_answer,
        objeto: m.required_object_type,
        distractores: m.distractors
    }));

    return `Genera 5 micro-relatos matemáticos conectados narrativamente.

CONTEXTO: ${contextId}

DATOS MATEMÁTICOS (USA ESTOS NÚMEROS EXACTAMENTE):
${JSON.stringify(mathSummary, null, 2)}

Recuerda: Los 5 problemas deben contar una historia progresiva dentro del contexto "${contextId}".`;
}

/**
 * Llama a Gemini API en modo JSON
 */
async function callGeminiWithJsonMode(prompt, systemInstruction) {
    if (typeof callGeminiAPI === 'undefined') {
        throw new Error('Función callGeminiAPI no disponible');
    }

    const url = `${GEMINI_CONFIG.endpoint}?key=${GEMINI_CONFIG.apiKey}`;

    const requestBody = {
        contents: [{
            parts: [{ text: prompt }]
        }],
        systemInstruction: {
            parts: [{ text: systemInstruction }]
        },
        generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 3048,
            responseMimeType: "application/json"
        },
        safetySettings: GEMINI_CONFIG.safetySettings
    };

    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`API Error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    const text = data.candidates?.[0]?.content?.parts?.[0]?.text;

    if (!text) {
        throw new Error('Sin respuesta del modelo AI');
    }

    // Parsear JSON (limpiar markdown si existe)
    const cleanText = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const parsed = JSON.parse(cleanText);

    return Array.isArray(parsed) ? parsed : [parsed];
}

/**
 * Combina datos matemáticos con narrativas generadas
 */
function mergeMathAndNarrative(mathData, narrativeData) {
    return mathData.map((math, index) => {
        const narrative = narrativeData.find(n => n.id === math.id) || narrativeData[index] || {};

        return {
            ...math,
            story_text: narrative.story_text || `Problema ${math.id}`,
            image_prompt: narrative.image_prompt || generateFallbackImagePrompt(math),
            question: narrative.question || '¿Cuántos en total?'
        };
    });
}

/**
 * Genera un prompt de imagen básico como fallback
 */
function generateFallbackImagePrompt(mathStructure) {
    const level = store.config?.level || 'beginner';
    const object = mathStructure.required_object_type;
    const quantity = mathStructure.operands[0];

    if (level === 'beginner') {
        return `${quantity} ${object} clearly separated and aligned in a row on a plain white background, cartoon style, simple illustration for children`;
    } else {
        return `A colorful scene with ${object}, child-friendly cartoon illustration, educational style`;
    }
}

/**
 * Aplica narrativas básicas sin IA (fallback)
 */
function applyBasicNarratives(mathData, contextId, language) {
    console.log('📝 [NarrativeAgent] Aplicando narrativas básicas (fallback)');

    const context = CONTEXTS.find(ctx => ctx.id === contextId);
    const contextName = context ? context?.title?.[language] : contextId;

    return mathData.map((math, index) => {
        const basicStory = generateBasicStory(math, contextId, contextName, language);

        return {
            ...math,
            story_text: basicStory.text,
            image_prompt: generateFallbackImagePrompt(math),
            question: basicStory.question
        };
    });
}

// ========================================
// PLANTILLAS VARIADAS PARA NARRATIVAS
// ========================================

// Listas de nombres (representación equilibrada)
const BOY_NAMES = ['Mateo', 'Hugo', 'Martín', 'Leo', 'Manuel', 'Lucas', 'Pablo', 'Alejandro', 'Álvaro', 'Thiago', 'Óliver', 'Daniel', 'Mario', 'Gabriel', 'Adrián', 'David', 'Gonzalo', 'Rafael', 'Julen', 'Antonio'];
const GIRL_NAMES = ['Sofía', 'Lucía', 'Martina', 'María', 'Vega', 'Julia', 'Olivia', 'Valeria', 'Mía', 'Lola', 'Triana', 'Carla', 'Carmen', 'Gala', 'Paula', 'Daniela', 'Rosa', 'Alba', 'Candela', 'Lara'];

/**
 * Selecciona un nombre aleatorio
 */
function getRandomName() {
    const allNames = [...BOY_NAMES, ...GIRL_NAMES];
    return allNames[Math.floor(Math.random() * allNames.length)];
}

/**
 * Obtiene la concordancia correcta para un objeto (género gramatical)
 */
function getGenderAgreement(obj) {
    // Objetos femeninos comunes
    const feminineWords = [
        'manzana', 'pera', 'naranja', 'fresa', 'sandía', 'ciruela', 'uva',
        'cesta', 'bolsa', 'caja', 'mesa', 'silla', 'galleta', 'tarta',
        'botella', 'lata', 'fruta', 'verdura', 'zanahoria', 'lechuga'
    ];

    const objLower = obj.toLowerCase();
    return feminineWords.some(f => objLower.includes(f)) ? 'f' : 'm';
}

/**
 * Convierte plural a singular (heurística simple)
 */
function getSingular(word) {
    if (word.endsWith('es') && word.length > 3) {
        return word.slice(0, -2);
    }
    if (word.endsWith('s') && word.length > 2) {
        return word.slice(0, -1);
    }
    return word;
}

/**
 * Adapta la pregunta al género y número del objeto con mejor redacción
 */
function generateQuestion(obj, quantity, isSubtraction = false) {
    const gender = getGenderAgreement(obj);
    const isPlural = quantity !== 1;

    let quantifier;
    if (gender === 'f') {
        quantifier = isPlural ? 'cuántas' : 'cuánta';
    } else {
        quantifier = isPlural ? 'cuántos' : 'cuánto';
    }

    // Ajustar el objeto al número
    const objForm = isPlural ? obj : getSingular(obj);

    if (isSubtraction) {
        // Para restas: añadir contexto de ubicación
        const locations = ['en la bolsa', 'en la cesta', 'en total'];
        const location = locations[Math.floor(Math.random() * locations.length)];
        const verb = isPlural ? 'quedan' : 'queda';
        return `¿${quantifier.charAt(0).toUpperCase() + quantifier.slice(1)} ${objForm} ${verb} ${location}?`;
    } else {
        // Para sumas: usar "habrá/tendrá ahora" o "hay en total"
        const sumPhrases = [
            `habrá ahora`,
            `hay en total`
        ];
        const phrase = sumPhrases[Math.floor(Math.random() * sumPhrases.length)];
        return `¿${quantifier.charAt(0).toUpperCase() + quantifier.slice(1)} ${objForm} ${phrase}?`;
    }
}


// ========================================
// GENERACIÓN CONTEXTUAL DE NARRATIVAS
// ========================================

/**
 * Selecciona elementos aleatorios de un array sin repetición
 */
function selectRandomElements(array, count) {
    const shuffled = [...array].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
}

/**
 * Genera una narrativa contextual usando las tablas de verbos y elementos
 */
function generateContextualStory(mathStructure, contextId, contextName, language) {
    // Normalizar nombre del contexto para evitar "En En..."
    // Si el nombre ya empieza por "En " (ej: "En la frutería"), lo quitamos
    // para que las plantillas que añaden "En " no lo dupliquen.
    let cleanContextName = contextName;
    if (cleanContextName && cleanContextName.toLowerCase().startsWith('en ')) {
        cleanContextName = cleanContextName.substring(3).trim();
    }
    // Ajustar mayúscula inicial si es necesario (opcional, pero estético)
    // cleanContextName = cleanContextName.charAt(0).toLowerCase() + cleanContextName.slice(1);

    const { logic_type, operands, required_object_type } = mathStructure;
    const [num1, num2] = operands;
    const obj = required_object_type;
    const singularObj = typeof getSingular !== 'undefined' ? getSingular(obj) : obj;

    // Obtener tabla contextual
    const contextTable = typeof CONTEXT_VERB_TABLES !== 'undefined' && CONTEXT_VERB_TABLES[contextId];

    if (!contextTable) {
        // Si no hay tabla, usar fallback genérico básico
        return generateGenericFallback(num1, num2, obj, logic_type);
    }

    // Seleccionar verbos y elementos del contexto
    const verbs = selectRandomElements(contextTable.verbs, 10); // Ampliar pool para mejor selección semántica
    const elements = selectRandomElements(contextTable.elements, 2);

    // Seleccionar nombres
    const name1 = getRandomName();
    let name2 = getRandomName();
    while (name2 === name1) {
        name2 = getRandomName();
    }

    let storyText = '';
    let question = '';

    // ========================================
    // PAEV TEMPLATES DEFINITION
    // ========================================
    const PAEV_TEMPLATES = {
        SUM: {
            'CA1': [ // Cambio (Añadir): Tiempo 1 -> Acción -> Tiempo 2
                (n1, n2, v1, v2, o1, o2, name1, name2) => `${name1} ${v1} ${n1} ${o1}. Después, ${v2} ${n2} ${o2} más.`,
                (n1, n2, v1, v2, o1, o2, name1, name2, ctx) => `En ${ctx}, ${name1} cuenta ${n1} ${o1}. Después ${v2} ${n2} ${o2} adicionales.`,
                (n1, n2, v1, v2, o1, o2, name1, name2, ctx) => `${name1} empieza con ${n1} ${o1}. Más tarde, ${v2} ${n2} ${o2} que se suman.`
            ],
            'CO1': [ // Combinación (Total): Estático, Parte + Parte = Todo
                (n1, n2, v1, v2, o1, o2, name1, name2) => `Hay ${n1} ${o1} y ${n2} ${o2}.`,
                (n1, n2, v1, v2, o1, o2, name1, name2, ctx) => `En ${ctx} veo ${n1} ${o1} y también ${n2} ${o2}.`,
                (n1, n2, v1, v2, o1, o2, name1, name2, ctx) => `${name1} tiene ${n1} ${o1} y ${name2} tiene ${n2} ${o2}.`,
                (n1, n2, v1, v2, o1, o2, name1, name2) => `Sobre la mesa hay ${n1} ${o1} rojos y ${n2} ${o2} azules.`
            ]
        },
        SUB: {
            'CA2': [ // Cambio (Quitar): Tiempo 1 -> Acción -> Tiempo 2
                (n1, n2, v1, vr, o1, o2, name1) => `${name1} tiene ${n1} ${o1}. Después ${vr} ${n2} ${o2}.`,
                (n1, n2, v1, vr, o1, o2, name1, name2, ctx) => `En ${ctx}, había ${n1} ${o1}. Pero ${name1} ${vr} ${n2}.`,
                (n1, n2, v1, vr, o1, o2, name1) => `${name1} empieza con ${n1} ${o1}. Más tarde pierde ${n2}.`
            ]
        }
    };

    if (logic_type === 'suma') {
        // Default to CA1 if undefined
        const paevType = mathStructure.paev_type || 'CA1';
        storyText = generateContextualSumStory(name1, name2, num1, num2, obj, singularObj, verbs, elements, cleanContextName, contextId, paevType);

        // Adjust question based on PAEV
        question = paevType === 'CO1'
            ? generateQuestion(obj, num1 + num2, false, 'total')
            : generateQuestion(obj, num1 + num2, false, 'future');

    } else if (logic_type === 'resta') {
        const paevType = mathStructure.paev_type || 'CA2'; // CM1 removed per request
        storyText = generateContextualSubtractionStory(name1, num1, num2, obj, singularObj, verbs, elements, cleanContextName, contextId, paevType);
        question = generateQuestion(obj, num1 - num2, true);
    } else {
        // Fallback
        storyText = `En ${cleanContextName}, hay ${num1} ${obj} y ${num2} ${obj}.`;
        question = `¿Cuántos en total?`;
    }

    return {
        text: storyText + ' ' + question,
        question: question
    };
}

/**
 * Genera narrativa de suma usando verbos y elementos contextuales y PAEV
 */
function generateContextualSumStory(name1, name2, num1, num2, obj, singularObj, verbs, elements, contextName, contextId, paevType) {
    // Seleccionar verbos semánticamente válidos
    let selectedVerb1 = null;
    let selectedVerb2 = null;

    if (typeof selectSemanticVerb !== 'undefined') {
        selectedVerb1 = selectSemanticVerb(contextId, obj, verbs, []);
        selectedVerb2 = selectSemanticVerb(contextId, obj, verbs, [selectedVerb1]);
    }

    // Fallbacks
    const verb1 = selectedVerb1 ? (typeof conjugarVerbo !== 'undefined' ? conjugarVerbo(selectedVerb1, name1) : selectedVerb1) : 'tiene';
    const verb2 = selectedVerb2 ? (typeof conjugarVerbo !== 'undefined' ? conjugarVerbo(selectedVerb2, name1) : selectedVerb2) : 'consigue';

    // Formatear objetos
    const obj1Form = typeof formaCorrecta !== 'undefined' ? formaCorrecta(num1, singularObj, obj) : (num1 === 1 ? singularObj : obj);
    const obj2Form = typeof formaCorrecta !== 'undefined' ? formaCorrecta(num2, singularObj, obj) : (num2 === 1 ? singularObj : obj);

    // Get Templates for specific PAEV type
    const templates = PAEV_TEMPLATES.SUM[paevType] || PAEV_TEMPLATES.SUM['CA1'];

    // Select helper
    const template = templates[Math.floor(Math.random() * templates.length)];

    return template(num1, num2, verb1, verb2, obj1Form, obj2Form, name1, name2, contextName);
}

/**
 * Genera narrativa de resta usando verbos y elementos contextuales y PAEV
 */
function generateContextualSubtractionStory(name1, num1, num2, obj, singularObj, verbs, elements, contextName, contextId, paevType) {
    // Seleccionar verbo de reducción
    let selectedReductionVerb = null;
    if (typeof selectReductionVerb !== 'undefined') {
        selectedReductionVerb = selectReductionVerb(contextId, obj, verbs, []);
    }

    const reductionVerb = selectedReductionVerb ? (typeof conjugarVerbo !== 'undefined' ? conjugarVerbo(selectedReductionVerb, name1) : selectedReductionVerb) : 'pierde';
    const verb1 = 'tiene';

    // Formatear objetos
    const obj1Form = typeof formaCorrecta !== 'undefined' ? formaCorrecta(num1, singularObj, obj) : (num1 === 1 ? singularObj : obj);
    const obj2Form = typeof formaCorrecta !== 'undefined' ? formaCorrecta(num2, singularObj, obj) : (num2 === 1 ? singularObj : obj);

    const templates = PAEV_TEMPLATES.SUB[paevType] || PAEV_TEMPLATES.SUB['CA2'];
    const template = templates[Math.floor(Math.random() * templates.length)];

    return template(num1, num2, verb1, reductionVerb, obj1Form, obj2Form, name1, null, contextName);
}

/**
 * Conjugación simple de verbos en 3ª persona singular presente
 */


/**
 * Fallback genérico cuando no hay tabla contextual
 */
function generateGenericFallback(num1, num2, obj, logic_type) {
    let storyText = '';
    let question = '';

    if (logic_type === 'suma') {
        storyText = `Hay ${num1} ${obj} y después aparecen ${num2} ${obj} más.`;
        question = generateQuestion(obj, num1 + num2, false);
    } else if (logic_type === 'resta') {
        storyText = `Había ${num1} ${obj} y después se pierden ${num2}.`;
        question = generateQuestion(obj, num1 - num2, true);
    } else {
        storyText = `Hay ${num1} ${obj} y ${num2} ${obj}.`;
        question = `¿Cuántos en total?`;
    }

    return {
        text: storyText + ' ' + question,
        question: question
    };
}

/**
 * Genera una narrativa básica sin IA usando generación contextual
 */
function generateBasicStory(mathStructure, contextId, contextName, language) {
    // Usar la nueva generación contextual
    return generateContextualStory(mathStructure, contextId, contextName, language);
}

// Exportar para uso global
if (typeof window !== 'undefined') {
    window.enrichWithNarrative = enrichWithNarrative;
}

console.log('✅ [NarrativeAgent] Módulo cargado');
